// ============================================================
// HOOKED — THE OUTFITTER MANIFEST
// ★ ONE SOURCE OF TRUTH ★  (FishManifest's sibling)
// Every rod, line, bobber, lure, garment, aura, and title —
// defined here and ONLY here. No special-case gear logic may
// ever live in a component. Add an item here → it exists
// everywhere: Wylly's shelves, the angler's body, the cast,
// the wait, the strike, the fight.
//
// EMPIRE LAWS IN FORCE:
//   THE DIAL LAW    — code speaks Empire, screen speaks Wharf.
//                     radiance (not "rarity"), sigil (not
//                     "source tag"). WOVEN: stats surface as
//                     HOOK / LINE / SCALES.
//   PROVENANCE LAW  — every grant carries a SourceSigil
//                     (STARTER / BOUGHT / EARNED), namespaced
//                     by source. Earned glows brighter.
//   RADIANCE TIERS  — the six, per the IE cosmetic schema:
//                     common · uncommon · rare · epic ·
//                     legendary · mythic.
//   SLOT SCHEMA     — wardrobe uses the IE 6-slot schema
//                     (head/face/body/back/aura/title) so
//                     unlocks port to the shared ecosystem
//                     later. Tackle (rod/line/bobber/lure) is
//                     HOOKED's genre-local gear lane.
//   CURRENCY LANE   — SCALES is HOOKED's own currency. HOOKED
//                     writes only SCALES, in its own delta-
//                     transacted lane. (Cross-game exchange, if
//                     it ever comes, happens at the Well — not
//                     here.)
//   DERIVE, DON'T STORE — equipped gear stays as ids; effective
//                     HOOK/LINE/reach derive at read time via
//                     tackleStats(). Store the input, compute
//                     the output.
// ============================================================

import { SD, GM } from "./theme";

// ── Radiance — the six tiers, one color each ─────────────────
export const RADIANCE = {
  common:    { label: "COMMON",    color: "#9c8f74" },
  uncommon:  { label: "UNCOMMON",  color: "#7dc98f" },
  rare:      { label: "RARE",      color: "#35d3e0" },
  epic:      { label: "EPIC",      color: "#a48ff5" },
  legendary: { label: "LEGENDARY", color: "#f5cd5e" },
  mythic:    { label: "MYTHIC",    color: "#ff9ad5" },
};

// ── Slots — tackle lane + the IE 6-slot wardrobe schema ──────
// kind decides which equipped map the id lives in.
export const SLOTS = [
  { id: "rod",    kind: "tackle",   label: "RODS",    optional: false },
  { id: "line",   kind: "tackle",   label: "LINES",   optional: false },
  { id: "bobber", kind: "tackle",   label: "BOBBERS", optional: false },
  { id: "lure",   kind: "tackle",   label: "LURES",   optional: false },
  { id: "head",   kind: "wardrobe", label: "HEAD",    optional: false },
  { id: "face",   kind: "wardrobe", label: "FACE",    optional: true  },
  { id: "body",   kind: "wardrobe", label: "BODY",    optional: false },
  { id: "back",   kind: "wardrobe", label: "BACK",    optional: true  },
  { id: "aura",   kind: "wardrobe", label: "AURAS",   optional: true  },
  { id: "title",  kind: "wardrobe", label: "TITLES",  optional: false },
];

// ══════════════════════════════════════════════════════════════
// THE CATALOG
// price — SCALES. price 0 + sigil STARTER = day-one kit.
// earn  — earned-only items (no price). Provenance: earned
//         glows brighter than bought.
// Stat fields (all optional, all derived via tackleStats):
//   lyt        — HOOK bonus (progress per clean beat)
//   lyf        — LINE bonus (tension capacity per cast)
//   shoreReach — max effective cast power from shore (base 70)
//   ringSlow   — strike ring duration multiplier (stacks ×)
//   waitMult   — wait-time multiplier (lures)
//   tierShift  — up-tier weight shift (lures — a lesser SWEETEN)
//   ilkBias    — draws that Ilk's fish toward the hook
// Render fields (consumed by getOutfit → AnglerVector):
//   coat [top,bottom] · cap/capBand · face kind · back kind +
//   backColor · aura color · lineColor
// ══════════════════════════════════════════════════════════════
export const OUTFITTER = {

  // ═ RODS — the arm of the thing ═
  rod: [
    { id: "driftwood_rod", name: "Driftwood Rod", glyph: "🎣", radiance: "common", price: 0,
      desc: "It floated here. Now it works here." },
    { id: "brass_ferrule", name: "Brass Ferrule", glyph: "🎣", radiance: "uncommon", price: 20, lyt: 2,
      desc: "A lantern-brass joint. The hook bites truer." },
    { id: "longcast_pine", name: "Longcast Pine", glyph: "🎣", radiance: "rare", price: 45, shoreReach: 85,
      desc: "Shore casts carry to the edge of THE DEEP." },
    { id: "wyrmspine_rod", name: "Wyrmspine Rod", glyph: "🐉", radiance: "epic", price: 100, lyt: 4,
      desc: "Cut from something that did not agree to it." },
    { id: "moonbent_cane", name: "Moonbent Cane", glyph: "🌙", radiance: "legendary", price: 200, lyt: 3, ringSlow: 1.1,
      desc: "Bends toward the moon. Strikes arrive unhurried." },
    { id: "undertow_gift", name: "The Undertow's Gift", glyph: "🌊", radiance: "mythic",
      earn: { type: "legend", text: "Witness S'olra's fight to its end" }, lyt: 6, shoreReach: 101,
      desc: "She left it on the boards. The shore forgets its limits." },
  ],

  // ═ LINES — the thread between you and it ═
  line: [
    { id: "hemp_line", name: "Hemp Line", glyph: "🧵", radiance: "common", price: 0, lineColor: "#bfe8f2",
      desc: "Honest rope, spun thin." },
    { id: "waxed_silk", name: "Waxed Silk", glyph: "🧵", radiance: "uncommon", price: 15, lyf: 10, lineColor: "#e6eef0",
      desc: "Slips through the water like a rumor." },
    { id: "silverstrand", name: "Silverstrand", glyph: "🧵", radiance: "rare", price: 40, lyf: 20, lineColor: "#b8c4c9",
      desc: "Moonlight, drawn to a wire." },
    { id: "phantom_thread", name: "Phantom Thread", glyph: "🕸️", radiance: "epic", price: 95, lyf: 30, lineColor: "#a48ff5",
      desc: "Half of it isn't there. That half never snaps." },
    { id: "goldwire", name: "Goldwire", glyph: "✨", radiance: "legendary", price: 190, lyf: 45, lineColor: "#f5cd5e",
      desc: "Wylly won't say where the gold came from." },
  ],

  // ═ BOBBERS — the little liar on the surface ═
  bobber: [
    { id: "cork_bob", name: "Cork Bob", glyph: "🎈", radiance: "common", price: 0, color: "#d9a83f",
      desc: "It bobs. That's the whole job." },
    { id: "lantern_bob", name: "Lantern Bob", glyph: "🏮", radiance: "uncommon", price: 18, color: "#f2a64e", ringSlow: 1.05,
      desc: "A small light on dark water. Strikes read easier." },
    { id: "moon_bob", name: "Moon Bob", glyph: "🌕", radiance: "rare", price: 42, color: "#e8f0dd", ringSlow: 1.1,
      desc: "The water mistakes it for the sky and slows down." },
    { id: "star_bob", name: "Star Bob", glyph: "⭐", radiance: "epic", price: 90, color: "#f5cd5e", ringSlow: 1.12,
      desc: "Fell. Kept working." },
    { id: "ghost_bob", name: "Ghost Bob", glyph: "👻", radiance: "legendary", price: 185, color: "#c9b8ff", ringSlow: 1.18,
      desc: "The fish can't tell if it's real. Neither can you." },
    { id: "wizard_bob", name: "The Dry Cap", glyph: "🎩", radiance: "mythic",
      earn: { type: "curio", text: "Pull a Wizard's Cap from THE DEEP" }, color: "#a48ff5", ringSlow: 1.2,
      desc: "A cap that was never wet, sized down for the water." },
  ],

  // ═ LURES — invitations, written in different hands ═
  lure: [
    { id: "bare_hook", name: "Bare Hook", glyph: "🪝", radiance: "common", price: 0,
      desc: "No promises. Some fish respect that." },
    { id: "silver_dart", name: "Silver Dart", glyph: "🐟", radiance: "uncommon", price: 16, ilkBias: "Space",
      desc: "Open-water drifters chase the flash." },
    { id: "ember_spin", name: "Ember Spin", glyph: "🔥", radiance: "uncommon", price: 16, ilkBias: "Dragon",
      desc: "The warm-current brawlers can't leave it alone." },
    { id: "veil_moth", name: "Veil Moth", glyph: "🦋", radiance: "rare", price: 38, ilkBias: "Phantom",
      desc: "Half-seen wings for half-seen fish." },
    { id: "tick_tock", name: "Tick-Tock Spoon", glyph: "🕰️", radiance: "rare", price: 38, ilkBias: "Time",
      desc: "It keeps a meter the old things remember." },
    { id: "honeyglow", name: "Honeyglow", glyph: "🍯", radiance: "epic", price: 92, waitMult: 0.75, tierShift: 8,
      desc: "Sweeter waters, shorter waits — every cast, unbidden." },
    { id: "abyss_caller", name: "Abyss Caller", glyph: "🫧", radiance: "legendary", price: 200, waitMult: 0.9, tierShift: 14,
      desc: "It hums a note only the big ones answer." },
  ],

  // ═ HEAD — what the wind takes first ═
  head: [
    { id: "wharf_cap", name: "Wharf Cap", glyph: "🧢", radiance: "common", price: 0, cap: "#a3773c", capBand: "#5c421f",
      desc: "Standard issue for anyone the dock trusts." },
    { id: "teal_cap", name: "Shallows Cap", glyph: "🧢", radiance: "uncommon", price: 12, cap: "#0f7d8f", capBand: "#0a2d33",
      desc: "Dyed in the drift." },
    { id: "violet_hood", name: "Violet Hood", glyph: "🥷", radiance: "rare", price: 30, cap: "#5b3fae", capBand: "#241650",
      desc: "For anglers who prefer to be rumors." },
    { id: "gold_crest_cap", name: "Gold Crest Cap", glyph: "👑", radiance: "epic", price: 80, cap: "#d9a83f", capBand: "#a97e28",
      desc: "Wylly salutes it. He won't say why." },
    { id: "moon_hood", name: "Moon Hood", glyph: "🌙", radiance: "legendary", price: 160, cap: "#e8f0dd", capBand: "#b8c4c9",
      desc: "Woven from the reflection, not the moon itself." },
  ],

  // ═ FACE — optional. The lake doesn't judge. ═
  face: [
    { id: "spectacles", name: "Wire Spectacles", glyph: "👓", radiance: "uncommon", price: 10, face: "spectacles",
      desc: "See the water the way it sees itself." },
    { id: "eyepatch", name: "Squall Patch", glyph: "🏴‍☠️", radiance: "uncommon", price: 10, face: "eyepatch",
      desc: "The other eye saw something. It's resting." },
    { id: "silver_mask", name: "Silver Mask", glyph: "🎭", radiance: "rare", price: 35, face: "mask",
      desc: "The fish deserve a little mystery too." },
  ],

  // ═ BODY — the coat carries the night ═
  body: [
    { id: "gold_coat", name: "Lantern Coat", glyph: "🧥", radiance: "common", price: 0, coat: ["#f5cd5e", "#a97e28"],
      desc: "The wharf's own gold, cut for weather." },
    { id: "teal_slicker", name: "Drift Slicker", glyph: "🧥", radiance: "uncommon", price: 14, coat: ["#35d3e0", "#0a2d33"],
      desc: "Rain gives up and goes around it." },
    { id: "rose_vest", name: "Rose Vest", glyph: "🦺", radiance: "rare", price: 32, coat: ["#ff87a0", "#6e2635"],
      desc: "Loud on the boards, invisible at sunset." },
    { id: "phantom_cloak", name: "Phantom Cloak", glyph: "🌫️", radiance: "epic", price: 85, coat: ["#a48ff5", "#241650"],
      desc: "The hem never quite touches the dock." },
    { id: "silver_mail", name: "Moonscale Mail", glyph: "🛡️", radiance: "legendary", price: 170, coat: ["#e6eef0", "#4e585c"],
      desc: "Scales given, not taken. There's a difference." },
    { id: "undertow_coat", name: "Undertow Coat", glyph: "🌊", radiance: "mythic",
      earn: { type: "catches", n: 25, text: "Land 25 fish" }, coat: ["#6fd3e8", "#051826"],
      desc: "Deep water, tailored. It remembers every fight." },
  ],

  // ═ BACK — optional. What you carry says who you are. ═
  back: [
    { id: "rope_coil", name: "Rope Coil", glyph: "🪢", radiance: "uncommon", price: 10, back: "coil", backColor: "#a3773c",
      desc: "You'll want it exactly once, badly." },
    { id: "creel_basket", name: "Creel Basket", glyph: "🧺", radiance: "uncommon", price: 12, back: "creel", backColor: "#c9974f",
      desc: "Optimism, woven." },
    { id: "net_bundle", name: "Net Bundle", glyph: "🕸️", radiance: "rare", price: 28, back: "net", backColor: "#b8c4c9",
      desc: "For the ones that don't respect the rod." },
    { id: "lantern_pack", name: "Lantern Pack", glyph: "🏮", radiance: "epic", price: 75, back: "lantern", backColor: "#f5cd5e",
      desc: "Your own small moon, portable." },
  ],

  // ═ AURAS — optional. The water notices. ═
  aura: [
    { id: "foam_wake", name: "Foam Wake", glyph: "💠", radiance: "uncommon", price: 25, aura: "#bfe8f2",
      desc: "The shallows follow you ashore." },
    { id: "ember_halo", name: "Ember Halo", glyph: "🔥", radiance: "rare", price: 55, aura: "#e8842f",
      desc: "Warm-current weather, personally." },
    { id: "phantom_veil", name: "Phantom Veil", glyph: "🌫️", radiance: "epic", price: 110, aura: "#a48ff5",
      desc: "You arrive slightly before yourself." },
    { id: "lantern_glow", name: "Lantern Glow", glyph: "🏮", radiance: "legendary", price: 220, aura: "#f5cd5e",
      desc: "The wharf's blessing, worn openly." },
    { id: "vaels_favor", name: "V'ael's Favor", glyph: "🜋", radiance: "mythic",
      earn: { type: "repute", n: 300, text: "Reach 300 REPUTE" }, aura: "#c9b8ff",
      desc: "Someone is watching your line with great interest." },
  ],

  // ═ TITLES — earned only. The dock keeps score. ═
  title: [
    { id: "dockhand", name: "DOCKHAND", glyph: "◆", radiance: "common", price: 0,
      desc: "Everyone starts on the boards." },
    { id: "drifter", name: "DRIFTER", glyph: "◆", radiance: "uncommon",
      earn: { type: "repute", n: 50, text: "Reach 50 REPUTE" },
      desc: "The mid-water knows your bobber by name." },
    { id: "deepcaller", name: "DEEPCALLER", glyph: "◆", radiance: "rare",
      earn: { type: "repute", n: 150, text: "Reach 150 REPUTE" },
      desc: "You cast past where the light agrees to go." },
    { id: "moontouched", name: "MOONTOUCHED", glyph: "✦", radiance: "epic",
      earn: { type: "flawless", text: "Land a FLAWLESS catch" },
      desc: "One perfect fight. The moon signed it." },
    { id: "undertow_witness", name: "THE UNDERTOW'S WITNESS", glyph: "🌊", radiance: "mythic",
      earn: { type: "legend", text: "Witness S'olra's fight to its end" },
      desc: "You were there. The water agrees." },
  ],
};

// ── TEST MODE — STANDING RULE (caP'n, July 20): ON UNTIL LAUNCH ─
// Every BUYABLE item costs 1 ❖ and a 100-❖ start stipend is
// granted, so the whole shelf is tryable AND the SCALES path
// (GAIN_SCALES → BUY_ITEM → ledger) is exercised end-to-end.
// This rewrites price at the flat-lookup layer only — the
// catalog above stays canon. Earned-only items (no price) are
// untouched; they still require their condition. Starter items
// (price 0) stay free.
//
// ⚠ STANDING ORDER: this stays TRUE until caP'n explicitly calls
// launch. ANY new item added before then inherits test pricing
// automatically (the rule keys off price>0, not a per-item flag),
// so no bookkeeping is needed when the catalog grows.
//   ↻ AT LAUNCH ONLY: set PRICE_TEST_MODE = false.
// (Pairs with the TEST_START_SCALES grant in angler.js — flip
//  both together, and only when caP'n says.)
export const PRICE_TEST_MODE = true;
const testPrice = (it) =>
  PRICE_TEST_MODE && !it.earn && (it.price ?? 0) > 0 ? { ...it, price: 1 } : it;

// ── Flat lookup — never scan the catalog in a component ──────
export const ITEMS = {};
Object.entries(OUTFITTER).forEach(([slot, arr]) =>
  arr.forEach(it => { ITEMS[it.id] = { ...testPrice(it), slot }; })
);
export const getItem = (id) => (id && ITEMS[id]) || null;

// ── Provenance helpers ───────────────────────────────────────
export const isUnlocked = (angler, id) =>
  !!id && (angler.unlocked || []).some(u => u.id === id);

// Earned-condition checker — checked against the ANGLER,
// never the board (Curio law's cousin).
export function earnMet(angler, item) {
  const e = item?.earn;
  if (!e) return false;
  switch (e.type) {
    case "repute":   return angler.renown >= e.n;
    case "catches":  return angler.catches.length >= e.n;
    case "legend":   return angler.catches.some(c => c.fishId === "solra");
    case "flawless": return angler.catches.some(c => c.grade === "FLAWLESS");
    case "curio":    return (angler.curios || []).length >= 1;
    default:         return false;
  }
}

// ── tackleStats — DERIVE, DON'T STORE ────────────────────────
// The one deriving function. Every combat/cast consumer reads
// effective values from here; nothing stores a computed stat.
export function tackleStats(angler) {
  const rod  = getItem(angler.tackle?.rod)    || {};
  const line = getItem(angler.tackle?.line)   || {};
  const bob  = getItem(angler.tackle?.bobber) || {};
  const lure = getItem(angler.tackle?.lure)   || {};
  return {
    lyt:         rod.lyt || 0,                          // +HOOK
    lyf:         line.lyf || 0,                         // +LINE
    shoreReach:  rod.shoreReach || 70,                  // shore power cap
    ringSlow:    (rod.ringSlow || 1) * (bob.ringSlow || 1),
    waitMult:    lure.waitMult || 1,
    tierShift:   lure.tierShift || 0,
    ilkBias:     lure.ilkBias || null,
    bobberGlyph: bob.glyph || "🎈",
    bobberColor: bob.color || GM.gold,
    lineColor:   line.lineColor || SD.foam,
  };
}

// ── getOutfit — resolve equipped ids → render props ──────────
// Consumed by PlayerEntity/AnglerVector and Wylly's preview.
export function getOutfit(angler) {
  const body = getItem(angler.wardrobe?.body) || {};
  const head = getItem(angler.wardrobe?.head) || {};
  const face = getItem(angler.wardrobe?.face);
  const back = getItem(angler.wardrobe?.back);
  const aura = getItem(angler.wardrobe?.aura);
  const line = getItem(angler.tackle?.line)   || {};
  return {
    coat:      body.coat || ["#f5cd5e", "#a97e28"],
    cap:       head.cap  || "#a3773c",
    capBand:   head.capBand || "#5c421f",
    face:      face?.face || null,
    back:      back?.back || null,
    backColor: back?.backColor || GM.rope,
    aura:      aura?.aura || null,
    lineColor: line.lineColor || SD.foam,
  };
}

// ── The SCALES economy — HOOKED's own currency lane ──────────
// Payout derives from the fish (tier) and the fight (grade).
const SCALE_BASE  = { 1: 3, 2: 7, 3: 14, 4: 40 };
const SCALE_GRADE = { FLAWLESS: 2, CLEAN: 1.5, HONEST: 1, RAGGED: 0.7, PROPHECY: 2 };
export function scalesForCatch(fish, grade) {
  const base = SCALE_BASE[fish.tier] ?? 3;
  const mult = SCALE_GRADE[grade] ?? 1;
  return Math.max(1, Math.round(base * mult));
}
export const SCALES_FOR_CURIO = 25;

// ── Day-one kit — granted in initialAngler with sigil STARTER ─
export const STARTER_KIT = [
  "driftwood_rod", "hemp_line", "cork_bob", "bare_hook",
  "wharf_cap", "gold_coat", "dockhand",
];
